import React, { useMemo, useState } from 'react';
import { Copy, Check } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../../../../components/ui/dialog';
import { Button } from '../../../../../components/ui/button';
import type { AuditTrailItem } from './auditTrailTypes';
import {
  getModuleLabel,
  getEventDisplayLabel,
  getActorDisplay,
  formatAuditTrailDateTime,
  parseActionDetails,
  getProjectDisplayName,
  getAuditSummary,
} from './auditTrailConstants';
import { SeverityBadge } from './auditTrailUi';

interface AuditTrailDetailSheetProps {
  item: AuditTrailItem | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[140px_1fr] gap-3 py-1.5 border-b border-[#EEF0F3] last:border-0">
      <span className="text-[11px] font-medium text-heritage-grey uppercase tracking-wide">{label}</span>
      <span className="text-[13px] text-text-grey break-words">{value ?? '—'}</span>
    </div>
  );
}

export const AuditTrailDetailSheet: React.FC<AuditTrailDetailSheetProps> = ({
  item,
  open,
  onOpenChange,
}) => {
  const [copied, setCopied] = useState(false);

  const actionBlock = useMemo(() => {
    if (!item) return { display: '—', isJson: false };
    return parseActionDetails(item.action_details);
  }, [item]);

  const handleCopy = async () => {
    if (!actionBlock.display || actionBlock.display === '—') return;
    try {
      await navigator.clipboard.writeText(actionBlock.display);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      /* ignore */
    }
  };

  if (!item) return null;

  const actor = getActorDisplay(item);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl w-[95vw] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-blueprint-blue">
            Audit event #{item.id}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded-lg border border-[#E2E5EA] p-4 bg-page-bg/50">
            <DetailRow label="When" value={formatAuditTrailDateTime(item.created_at)} />
            <DetailRow label="Severity" value={<SeverityBadge severity={item.severity} />} />
            <DetailRow label="Module" value={getModuleLabel(item.module)} />
            <DetailRow label="Session" value={item.session_id} />
            <DetailRow label="Event" value={getEventDisplayLabel(item)} />
            <DetailRow label="Project" value={getProjectDisplayName(item)} />
            <DetailRow label="Summary" value={getAuditSummary(item)} />
          </div>

          <div>
            <h4 className="font-heading text-[13px] font-semibold text-blueprint-blue mb-2">Actor</h4>
            <div className="rounded-lg border border-[#E2E5EA] p-4">
              <DetailRow label="Display" value={actor.primary} />
              <DetailRow label="Email / ID" value={item.actor_email || item.actor_id} />
              {item.actor_name && item.actor_name !== actor.primary && (
                <DetailRow label="Name (raw)" value={item.actor_name} />
              )}
            </div>
          </div>

          <div>
            <h4 className="font-heading text-[13px] font-semibold text-blueprint-blue mb-2">Entity</h4>
            <div className="rounded-lg border border-[#E2E5EA] p-4">
              <DetailRow label="Type" value={item.entity_type} />
              <DetailRow label="ID" value={item.entity_id} />
              <DetailRow label="Name" value={item.entity_name} />
              <DetailRow label="Email" value={item.entity_email} />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-heading text-[13px] font-semibold text-blueprint-blue">
                Action details
              </h4>
              {actionBlock.display !== '—' && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-8 text-[12px] gap-1"
                  onClick={() => void handleCopy()}
                >
                  {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                  {copied ? 'Copied' : 'Copy'}
                </Button>
              )}
            </div>
            <pre className="text-[11px] text-text-grey bg-page-bg border border-[#E2E5EA] rounded-lg p-3 overflow-x-auto max-h-64 whitespace-pre-wrap font-mono">
              {actionBlock.display}
            </pre>
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
